/**
 * OrderingPrint - Figma-matching print renderer for ordering quizzes
 * Layout: PrintQuizTitle → image → word bank (card-style items with ① numbers) → order boxes (56x56 → arrows)
 * Only "single-line" variant implemented per spec.
 */

import { cn } from '../../../lib/utils'
import { KatexText } from '../shared'
import type { OrderingQuizData } from '../../../types/legacyQuiz'
import { PrintQuizTitle, PrintQuizProblem, PrintQuizImage, PrintOptionImage, PrintOrderBoxes, getStyles, getImageUrl, getPrintScale } from './print-common'

// Circled numbers ①②③④⑤⑥⑦⑧⑨⑩
const CIRCLED_NUMBERS = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩']

interface PrintQuizProps {
  quizId: string | number
  quiz: OrderingQuizData
  number: number
  showCorrectAnswers?: boolean
  correctAnswers?: string[]
  compact?: boolean
}

export function OrderingPrint({
  quizId,
  quiz,
  number,
  showCorrectAnswers = false,
  correctAnswers,
  compact,
}: PrintQuizProps) {
  const s = getStyles(compact)
  const imageUrl = getImageUrl(quiz.image, quiz.imagePrint)
  const printScale = getPrintScale(quiz.image, quiz.imagePrint)

  // Build correct order mapping: position index -> correct order number
  const correctOrderMap = new Map<number, number>()
  if (showCorrectAnswers && correctAnswers) {
    correctAnswers.forEach((key, idx) => {
      const choiceIdx = quiz.choices.findIndex(c => c.key === key)
      if (choiceIdx !== -1) {
        correctOrderMap.set(idx, choiceIdx + 1)
      }
    })
  }

  return (
    <div className={cn(s.container, s.quizSection)}>
      {/* Title */}
      <PrintQuizTitle number={number} compact={compact}>
        <KatexText text={quiz.title} />
      </PrintQuizTitle>

      {/* Problem body (with {image=N} → additionalImages) */}
      <PrintQuizProblem
        problem={quiz.problem}
        quizId={quizId}
        additionalImages={quiz.additionalImages}
        compact={compact}
      />

      {/* Image */}
      {imageUrl && <PrintQuizImage src={imageUrl} printScale={printScale} />}

      {/* Word bank - card style items */}
      <div className="pl-[40px] w-full">
        <div className={`relative bg-[#f2f2f2] rounded-[16px] ${compact ? 'pt-[38px] pb-[16px] px-[20px]' : 'pt-[48px] pb-[24px] px-[24px]'}`}>
          {/* "보기" badge */}
          <span className={`absolute top-0 ${compact ? 'left-[20px] w-[54px] justify-center' : 'left-[24px]'} bg-[#999] text-white h-[28px] rounded-bl-[8px] rounded-br-[8px] px-[12px] flex items-center text-[14px] font-bold`}>
            보기
          </span>
          {/* Choice cards - layout based on subtype */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: quiz.subtype === 'full'
              ? '1fr'
              : quiz.subtype === '2by2'
                ? 'repeat(2, 1fr)'
                : `repeat(${quiz.choices.length}, 1fr)`,
            gap: 16,
          }}>
            {quiz.choices.map((choice, idx) => {
              const choiceImageUrl = getImageUrl(choice.image)
              return (
                <div
                  key={choice.key}
                  className={cn(
                    s.inputBoxSm,
                    `flex items-center gap-[8px] px-[12px] w-full h-auto! ${(quiz.subtype === 'full' || quiz.subtype === '2by2') ? (compact ? 'min-h-[35px]' : 'min-h-[40px]') : 'min-h-[40px]'}`
                  )}
                >
                  <span className="text-[20px] text-black shrink-0">
                    {CIRCLED_NUMBERS[idx] || `${idx + 1}`}
                  </span>
                  {choiceImageUrl && (
                    <PrintOptionImage src={choiceImageUrl} alt="item" baseMaxHeightPx={28} />
                  )}
                  {choice.answer && (
                    <span className="text-[16px] text-black tracking-[-0.096px]">
                      <KatexText text={choice.answer} />
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Order boxes with arrows */}
      <div className="w-full flex justify-center">
        <PrintOrderBoxes
          count={quiz.choices.length}
          correctOrder={correctOrderMap}
          showCorrectAnswers={showCorrectAnswers}
          compact={compact}
        />
      </div>
    </div>
  )
}
